import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Icon } from '@/icons';
import { StatsStrip } from './StatsStrip';
import feedShot from '../assets/feed-phone-v1.png';

const Wrap = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.32fr) minmax(230px, 0.88fr);
  align-items: center;
  gap: 26px;
  min-width: 0;
  padding: 24px 10px 24px 24px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 32px 16px;
  }
  @media (max-width: 767.98px) {
    padding: 32px 4px 20px;
    gap: 32px;
  }
`;
const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;
const Kicker = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.8;
  font-weight: 500;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Headline = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(42px, 4.7vw, 78px);
  font-weight: 750;
  line-height: 1.02;
  letter-spacing: -0.045em;
  color: ${({ theme }) => theme.color.textPrimary};
  span {
    display: block;
    padding-bottom: 0.08em;
    background: linear-gradient(
      110deg,
      ${({ theme }) => theme.color.accentCyan},
      ${({ theme }) => (theme.mode === 'dark' ? '#EBA7F1' : '#843C9A')}
    );
    background-clip: text;
    color: transparent;
  }
  @media (max-width: 767.98px) {
    font-size: clamp(38px, 10vw, 64px);
  }
  @media (forced-colors: active) {
    span {
      background: none;
      color: CanvasText;
    }
  }
`;
const Sub = styled.p`
  margin: 0;
  max-width: 51ch;
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
const ctaBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 50px;
  padding: 12px 20px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 650;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 160ms ease,
    transform 160ms ease;
  &:hover {
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;
const PrimaryCta = styled(Link)`
  ${ctaBase}
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  box-shadow: 0 5px 24px ${({ theme }) => theme.color.accentCyan}24;
`;
const OutlineCta = styled(Link)`
  ${ctaBase}
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  background: ${({ theme }) => theme.color.bgRoot}B8;
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover {
    background: ${({ theme }) => theme.color.surfaceRaised};
  }
`;
const AppCta = styled.a`
  ${ctaBase}
  align-self: flex-start;
  margin-top: -10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.bgRoot}B8;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Product = styled.figure`
  margin: 0;
  min-width: 0;
  width: 100%;
  max-width: 310px;
  justify-self: center;
  position: relative;
  perspective: 1400px;
  &::before {
    content: '';
    position: absolute;
    inset: 12% -12%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      ${({ theme }) => theme.color.accentCyan}45,
      transparent 65%
    );
    pointer-events: none;
  }
  @media (max-width: 1100px) {
    max-width: 300px;
  }
  @media (max-width: 767.98px) {
    max-width: 280px;
  }
`;
const Phone = styled(Link)`
  display: block;
  position: relative;
  padding: 9px;
  border-radius: 42px;
  background: linear-gradient(125deg, #aea0b8, #211a29 16%, #0a090d 55%, #786080);
  border: 1px solid #ad93bb;
  transform: rotateY(-8deg) rotateZ(2deg);
  box-shadow:
    0 24px 60px #0008,
    0 0 26px ${({ theme }) => theme.color.accentCyan}38;
  &::after {
    content: '';
    position: absolute;
    top: 8px;
    left: 33%;
    width: 34%;
    height: 16px;
    border-radius: 0 0 14px 14px;
    background: #09090d;
  }
  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 33px;
    border: 2px solid #08070b;
  }
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 6px;
  }
`;
const Caption = styled.figcaption`
  position: relative;
  text-align: center;
  margin-top: 18px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.textMuted};
`;

export function LandingHero() {
  return (
    <Wrap aria-labelledby="landing-headline">
      <Copy>
        <Kicker>Freedom to speak, safety you can trust, privacy you control</Kicker>
        <Headline id="landing-headline">
          Moderation <span>you can see.</span>
        </Headline>
        <Sub>
          TruXSocial is an independent, free-speech social platform. Five rules, nothing else, and
          when one of them is enforced, the decision is made by a randomly selected jury of real
          members, in public, with written reasons. Not a black box of silent bans.
        </Sub>
        <Actions>
          <PrimaryCta to="/signup">
            Create account <Icon name="share" size={20} />
          </PrimaryCta>
          <OutlineCta to="/feed">
            <Icon name="compass" size={20} />
            Browse the feed
          </OutlineCta>
        </Actions>
        <AppCta href="#get-the-app">
          <Icon name="download" size={20} />
          Get the Android app
        </AppCta>
        <StatsStrip />
      </Copy>
      <Product>
        <Phone to="/feed" aria-label="Explore the mobile feed preview">
          <img
            src={feedShot}
            width="780"
            height="1640"
            alt="The TruXSocial feed with individual post cards and reply, cherish, like and share actions"
          />
        </Phone>
        <Caption>The live interface, shown with example posts.</Caption>
      </Product>
    </Wrap>
  );
}
